import React, { useState, useEffect, useRef, useMemo } from "react";
import { Dropdown, Form } from "react-bootstrap";

const MultiSelectDropdown = ({
  selectedValues = [],
  picklistValues = [],
  onChange,
  onClose,
  disabled,
  placeholder = "Select...",
  isRequired = false,
  forceOpen = false,
  /** Keys that cannot be unchecked (e.g. objects required by an enabled plan package). */
  lockedKeys = [],
}) => {
  const [show, setShow] = useState(false);
  const [localSelectedValues, setLocalSelectedValues] = useState(selectedValues);
  const dropdownRef = useRef(null);
  const lockedSet = useMemo(
    () => new Set((lockedKeys || []).map((k) => String(k))),
    [lockedKeys]
  );

  // Sync local state with prop changes
  useEffect(() => {
    setLocalSelectedValues(selectedValues);
  }, [selectedValues]);

  // Sync show state with forceOpen prop
  useEffect(() => {
    if (forceOpen !== undefined) {
      setShow(forceOpen);
    }
  }, [forceOpen]);


  const toggleValue = (key) => {
    const keyStr = String(key);
    const had = localSelectedValues.some((k) => String(k) === keyStr);
    if (had && lockedSet.has(keyStr)) {
      return;
    }
    const updated = had
      ? localSelectedValues.filter((k) => String(k) !== keyStr)
      : [...localSelectedValues, keyStr];

    // Update local state immediately for instant UI feedback
    setLocalSelectedValues(updated);

    // Notify parent component
    if (onChange) {
      onChange(updated);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    // Don't add click outside handler if forceOpen is true (controlled externally)
    if (forceOpen || !show) {
      return;
    }

    const handleClickOutside = (event) => {
      // Check if click is inside the dropdown container
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return; // Click is inside, don't close
      }

      // Click is outside, close the dropdown
      setShow(false);
      if (onClose) {
        onClose();
      }
    };

    // Use a small delay to avoid immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [show, onClose, forceOpen]);




  const renderLabel = (key) => {
    const found = picklistValues.find((opt) => String(opt.key) === String(key));
    let base;
    if (found?.value) base = found.value;
    else if (key != null && String(key).trim()) {
      const s = String(key);
      base = s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim() || s;
    } else base = "[Unknown]";
    if (lockedSet.has(String(key))) return `${base} (required)`;
    return base;
  };

  return (
    <div
      ref={dropdownRef}
      className="position-relative"
      style={{ width: "100%" }}
    >
      <Dropdown
        show={show}
        autoClose={false}
        onToggle={(isOpen) => {
          // Don't allow manual toggle if forceOpen is true (controlled externally)
          if (forceOpen && !isOpen) {
            return;
          }
          setShow(isOpen);
          if (!isOpen && onClose) {
            onClose();
          }
        }}
      >
        <Dropdown.Toggle
          variant="outline-secondary"
          className="w-100 text-start form-control bg-white border d-flex justify-content-between align-items-center"
          disabled={disabled}
          style={{
            color: disabled ? "#6c757d" : "#212529", // Matches Bootstrap input color
            fontWeight: "400",
            padding: "0.375rem 0.75rem",
            height: "calc(1.5em + 0.75rem + 2px)", // Standard Bootstrap form-control height
            minHeight: "38px", // Ensure minimum height matches other inputs
            lineHeight: "1.5",
            fontSize: "1rem",
            border: "1px solid #ced4da", // Match Bootstrap input border
            borderRadius: "0.375rem", // Match Bootstrap input border radius
            backgroundColor: "#fff", // Ensure white background
            boxShadow: "none" // Remove any default dropdown shadows
          }}
        >
          <span
            className="text-truncate"
            style={{
              maxWidth: "90%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {localSelectedValues.length > 0
              ? localSelectedValues.map((key) => renderLabel(key)).join(", ")
              : placeholder}
          </span>
          <span
            className="ms-2 dropdown-caret"
            style={{ color: "#212529", fontSize: "0.8rem" }}
          >
            ▼
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="w-100 mt-1 kanban-filter-dropdown-menu"
          style={{
            maxHeight: "250px",
            overflowY: "auto",
            zIndex: 10050,
            position: 'absolute'
          }}
        >
          {picklistValues.length > 0 ? (
            picklistValues.map((opt) => {
              const optKeyStr = String(opt.key);
              const isChecked = localSelectedValues.some((k) => String(k) === optKeyStr);
              const isLocked = lockedSet.has(optKeyStr);
              const rowDisabled = disabled || isLocked;
              const labelText = isLocked ? `${opt.value} (required)` : opt.value;

              return (
                <div
                  key={optKeyStr}
                  className="dropdown-item-text"
                  style={{
                    padding: '0.5rem 1rem',
                    cursor: rowDisabled ? 'not-allowed' : 'pointer'
                  }}
                  onClick={(e) => {
                    // Prevent event from bubbling up
                    e.stopPropagation();
                    // Don't toggle if disabled
                    if (!disabled && !isLocked) {
                      toggleValue(opt.key);
                    }
                  }}
                >
                  <Form.Check
                    type="checkbox"
                    label={labelText}
                    checked={isChecked}
                    onChange={(e) => {
                      // Prevent event from bubbling up
                      e.stopPropagation();
                      if (!disabled && !isLocked) {
                        toggleValue(opt.key);
                      }
                    }}
                    onClick={(e) => {
                      // Prevent checkbox click from bubbling
                      e.stopPropagation();
                    }}
                    disabled={disabled || isLocked}
                    className="text-dark"
                  />
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item-text text-muted text-center py-2"
              style={{ cursor: "default", padding: '0.5rem 1rem' }}
            >
              <small>
                {disabled ? "No values available" : "No options to select"}
              </small>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default MultiSelectDropdown;
